// src/components/CalendarSelector.jsx
import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Check, X, Calendar as CalendarIcon, RefreshCw } from 'lucide-react';
import { SkeletonList } from './SkeletonLoader';
import Modal from './Modal';
import styles from './CalendarSelector.module.css';

const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

const PROVIDER_BADGE_LABEL = { google: 'Google', microsoft: 'Microsoft', apple: 'Apple' };
const PROVIDER_BADGE_CLASS = { google: styles.badgeGoogle, microsoft: styles.badgeMicrosoft, apple: styles.badgeApple };

export default function CalendarSelector({ onClose, onSave }) {
  const [calendars, setCalendars] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { loadCalendars(); }, []);

  const loadCalendars = async () => {
    try {
      setLoading(true); setError(null);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const response = await fetch(`${API_URL}/v1/calendars`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch calendars');
      const data = await response.json();
      let allCalendars = [];
      if (data.providers && Array.isArray(data.providers)) {
        allCalendars = data.providers.flatMap(p => p.calendars || []);
      } else if (data.calendars) {
        allCalendars = data.calendars;
      }
      setCalendars(allCalendars);
      setSelectedIds(new Set(allCalendars.filter(c => c.is_selected).map(c => c.id)));
    } catch (err) {
      console.error('Error loading calendars:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleCalendar = (id) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedIds(next);
  };

  const handleSelectAll = () => setSelectedIds(new Set(calendars.map(c => c.id)));
  const handleDeselectAll = () => setSelectedIds(new Set());

  const handleSave = async () => {
    try {
      setSaving(true); setError(null);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const selected = calendars.filter(c => selectedIds.has(c.id));
      const byProvider = selected.reduce((acc, c) => {
        const p = c.provider || 'google';
        if (!acc[p]) acc[p] = [];
        acc[p].push(c.id);
        return acc;
      }, {});
      const responses = await Promise.all(
        Object.entries(byProvider).map(([provider, ids]) =>
          fetch(`${API_URL}/v1/calendars/selection`, {
            method: 'PUT',
            headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ calendar_ids: ids, provider })
          })
        )
      );
      if (responses.some(r => !r.ok)) throw new Error('Failed to save calendar selection');
      if (onSave) onSave(Array.from(selectedIds));
      onClose();
    } catch (err) {
      console.error('Error saving selection:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Select Calendars to Sync" size="md" scrollable={false}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.headerTitle}>
            <CalendarIcon size={24} className={styles.headerIcon} aria-hidden="true" />
            Select Calendars to Sync
          </h2>
          <p className={styles.headerSubtitle}>Choose which calendars to include in your availability</p>
        </div>
        <button onClick={onClose} className={styles.closeBtn} aria-label="Close"><X size={24} /></button>
      </div>

      <div className={styles.body}>
        {error && <div className={styles.errorBox}><p>{error}</p></div>}

        {loading ? (
          <SkeletonList items={5} />
        ) : calendars.length === 0 ? (
          <div className={styles.empty}>
            <CalendarIcon size={64} className={styles.emptyIcon} aria-hidden="true" />
            <p className={styles.emptyText}>No calendars found</p>
            <button onClick={loadCalendars} className={styles.retryBtn}>
              <RefreshCw size={16} aria-hidden="true" /> Retry
            </button>
          </div>
        ) : (
          <>
            <div className={styles.bulkActions}>
              <button onClick={handleSelectAll} className={styles.selectAllBtn}>Select All</button>
              <button onClick={handleDeselectAll} className={styles.deselectBtn}>Deselect All</button>
              <span className={styles.countText}>{selectedIds.size} of {calendars.length} selected</span>
            </div>

            <div className={styles.calList}>
              {calendars.map(cal => {
                const isSelected = selectedIds.has(cal.id);
                return (
                  <button
                    key={cal.id}
                    onClick={() => toggleCalendar(cal.id)}
                    className={`${styles.calItem}${isSelected ? ` ${styles.calItemSelected}` : ''}`}
                  >
                    <div className={`${styles.calCheck}${isSelected ? ` ${styles.calCheckSelected}` : ''}`}>
                      {isSelected && <Check size={16} aria-hidden="true" />}
                    </div>
                    {cal.backgroundColor && (
                      <div className={styles.calColor} style={{ backgroundColor: cal.backgroundColor }} />
                    )}
                    <div className={styles.calInfo}>
                      <div className={styles.calName}>
                        {cal.name}
                        {cal.primary && <span className={styles.badgePrimary}>Primary</span>}
                        {cal.provider && (
                          <span className={PROVIDER_BADGE_CLASS[cal.provider] || styles.badgeMicrosoft}>
                            {PROVIDER_BADGE_LABEL[cal.provider] || cal.provider}
                          </span>
                        )}
                      </div>
                      {cal.description && <p className={styles.calDesc}>{cal.description}</p>}
                      <p className={styles.calAccess}>Access: {cal.accessRole}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      <div className={styles.footer}>
        <button onClick={onClose} disabled={saving} className={styles.btnCancel}>Cancel</button>
        <button onClick={handleSave} disabled={saving || selectedIds.size === 0} className={`${styles.btnSave} btn-primary`}>
          {saving ? (
            <><RefreshCw size={16} className={styles.spinning} aria-hidden="true" /> Saving...</>
          ) : (
            `Save Selection (${selectedIds.size})`
          )}
        </button>
      </div>
    </Modal>
  );
}
